import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'app-laft-pep-form',
  templateUrl: './laft-pep-form.component.html',
  styleUrls: ['./laft-pep-form.component.scss'],
})
export class LaftPepFormComponent implements OnInit {
  @Output() nextStep = new EventEmitter();
  @Output() formChangesEvent = new EventEmitter();
  @Input() initData: any;

  formSubmitSubject$ = new Subject();
  public pepDataForm: FormGroup;
  public showFamilyMembers = false;
  public showFamily2ndDegreeMembers = false;
  public showPublicInstitutionName = false;

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.pepDataForm = this.formBuilder.group({
      isPEP: [false, [Validators.required]],
      hasPEPRelatives: [false, [Validators.required]],
      hasBeenWorkingOnPublicInstitution: [false, [Validators.required]],
      positionPartner: ['', []],
      institutionName: ['', []],
      partnerName: ['', []],
      familyMembers: this.formBuilder.array([]),
      family2ndDegreeMembers: this.formBuilder.array([]),
    });

    this.pepDataForm.get('hasBeenWorkingOnPublicInstitution')?.valueChanges.subscribe(value => {
      this.showPublicInstitutionName = value;
      const institutionNameControl = this.pepDataForm.get('institutionName');
      const positionPartnerControl = this.pepDataForm.get('positionPartner');
      if (this.showPublicInstitutionName) {
        institutionNameControl?.setValidators([Validators.required]);
        positionPartnerControl?.setValidators([Validators.required]);
      } else {
        institutionNameControl?.clearValidators();
        positionPartnerControl?.clearValidators();
        institutionNameControl?.setValue('');
        positionPartnerControl?.setValue('');
      }

      institutionNameControl?.updateValueAndValidity();
      positionPartnerControl?.updateValueAndValidity();
    });
    this.pepDataForm.get('hasPEPRelatives')?.valueChanges.subscribe(value => {
      this.showFamilyMembers = value;
      if (value && this.familyMembers.length === 0) {
        this.addFamilyMember();
      }

      // Update validators based on hasPEPRelatives value
      this.familyMembers.controls.forEach((control, index) => {
        const fullNameControl = control.get('fullName');
        const relationshipControl = control.get('relationship');
        const partnerNameControl = this.pepDataForm.get('partnerName');

        if (value && index === 0) {
          // First member is required when "Sí" is selected
          fullNameControl?.setValidators([Validators.required]);
          relationshipControl?.setValidators([Validators.required]);
        } else {
          // Remove validators for "No" or additional members
          fullNameControl?.clearValidators();
          relationshipControl?.clearValidators();
          partnerNameControl?.clearValidators();
        }

        fullNameControl?.updateValueAndValidity();
        relationshipControl?.updateValueAndValidity();
        partnerNameControl?.updateValueAndValidity();
      });

      if (!value) {
        this.familyMembers.clear();
      }

      this.familyMembers.updateValueAndValidity();
    });

    this.pepDataForm.get('isPEP')?.valueChanges.subscribe(value => {
      const hasBeenWorkingOnPublicInstitutionControl = this.pepDataForm.get('hasBeenWorkingOnPublicInstitution');
      if (value) {
        hasBeenWorkingOnPublicInstitutionControl?.setValidators([Validators.required]);
      } else {
        hasBeenWorkingOnPublicInstitutionControl?.clearValidators();
        hasBeenWorkingOnPublicInstitutionControl?.setValue(false);
      }
      hasBeenWorkingOnPublicInstitutionControl?.updateValueAndValidity();
      this.showFamily2ndDegreeMembers = value;
      if (value && this.family2ndDegreeMembers.length === 0) {
        this.add2ndDegreeMember();
      }

      this.family2ndDegreeMembers.controls.forEach((control, index) => {
        const fullNameControl = control.get('fullName');
        const relationshipControl = control.get('relationship');
        const partnerNameControl = this.pepDataForm.get('partnerName');

        if (value && index === 0) {
          fullNameControl?.setValidators([Validators.required]);
          relationshipControl?.setValidators([Validators.required]);
        } else {
          fullNameControl?.clearValidators();
          relationshipControl?.clearValidators();
          partnerNameControl?.clearValidators();
          partnerNameControl?.setValue('');
        }

        fullNameControl?.updateValueAndValidity();
        relationshipControl?.updateValueAndValidity();
        partnerNameControl?.updateValueAndValidity();
      });

      if (!value) {
        this.family2ndDegreeMembers.clear();
      }

      this.family2ndDegreeMembers.updateValueAndValidity();
    });

    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.pepDataForm.markAsDirty();
          this.pepDataForm.markAllAsTouched();

          this.familyMembers.controls.forEach(control => {
            control.markAsDirty();
            control.markAllAsTouched();
            control.updateValueAndValidity();
          });

          this.family2ndDegreeMembers.controls.forEach(control => {
            control.markAsDirty();
            control.markAllAsTouched();
            control.updateValueAndValidity();
          });
        }),
        switchMap(() =>
          this.pepDataForm.statusChanges.pipe(
            startWith(this.pepDataForm.status),
            filter(status => status !== 'PENDING'),
            take(1)
          )
        ),
        filter(status => status === 'VALID')
      )
      .subscribe(() => {
        const formValue = this.getFormValueWithoutEmptyFields();
        this.nextStep.next(formValue);
      });

    this.pepDataForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      const formValue = this.getFormValueWithoutEmptyFields();
      this.formChangesEvent.emit(formValue);
    });
    this.populateForm();
  }

  get familyMembers(): FormArray {
    return this.pepDataForm.get('familyMembers') as FormArray;
  }

  get family2ndDegreeMembers(): FormArray {
    return this.pepDataForm.get('family2ndDegreeMembers') as FormArray;
  }

  addFamilyMember(): void {
    const isFirstMember = this.familyMembers.length === 0;
    this.familyMembers.push(this.createFamilyMemberFormGroup(isFirstMember));
  }

  add2ndDegreeMember(): void {
    const isFirstMember = this.family2ndDegreeMembers.length === 0;
    this.family2ndDegreeMembers.push(this.createFamilyMemberFormGroup(isFirstMember));
  }

  createFamilyMemberFormGroup(isRequired: boolean = false): FormGroup {
    return this.formBuilder.group({
      fullName: ['', isRequired ? [Validators.required] : []],
      relationship: ['', isRequired ? [Validators.required] : []],
    });
  }

  getFormValueWithoutEmptyFields() {
    const formValue = { ...this.pepDataForm.value };

    if (formValue.hasPEPRelatives && formValue?.familyMembers && Array.isArray(formValue?.familyMembers)) {
      formValue.familyMembers = formValue.familyMembers.filter(
        (member: any) => member.fullName?.trim() && member.relationship?.trim()
      );
      if (formValue.familyMembers.length === 0) {
        formValue.familyMembers = [];
      }
    } else {
      formValue.familyMembers = [];
    }

    if (formValue.isPEP && formValue?.family2ndDegreeMembers && Array.isArray(formValue?.family2ndDegreeMembers)) {
      formValue.family2ndDegreeMembers = formValue.family2ndDegreeMembers.filter(
        (member: any) => member.fullName?.trim() && member.relationship?.trim()
      );
      if (formValue.family2ndDegreeMembers.length === 0) {
        formValue.family2ndDegreeMembers = [];
      }
    } else {
      formValue.family2ndDegreeMembers = [];
    }

    if (!formValue.hasBeenWorkingOnPublicInstitution) {
      formValue.institutionName = '';
      formValue.positionPartner = '';
    }

    if (!formValue.partnerName || formValue.partnerName.trim() === '') {
      formValue.partnerName = '';
    }

    return formValue;
  }

  onContinue(): void {
    this.formSubmitSubject$.next();
  }

  private populateForm(): void {
    this.pepDataForm.patchValue({
      isPEP: this.initData?.isPEP ?? false,
      hasPEPRelatives: this.initData?.hasPEPRelatives ?? false,
      hasBeenWorkingOnPublicInstitution: this.initData?.hasBeenWorkingOnPublicInstitution ?? false,
      positionPartner: this.initData?.positionPartner ?? '',
      institutionName: this.initData?.institutionName ?? '',
      partnerName: this.initData?.partnerName ?? '',
    });

    const familyMembersArray = this.familyMembers;
    familyMembersArray.clear();
    if (Array.isArray(this.initData?.familyMembers)) {
      this.initData.familyMembers.forEach((member: any, idx: number) => {
        const group = this.createFamilyMemberFormGroup(idx === 0 && this.pepDataForm.get('hasPEPRelatives')?.value);
        group.patchValue({
          fullName: member.fullName ?? '',
          relationship: member.relationship ?? '',
        });
        familyMembersArray.push(group);
      });
    }

    const family2ndDegreeArray = this.family2ndDegreeMembers;
    family2ndDegreeArray.clear();
    if (Array.isArray(this.initData?.family2ndDegreeMembers)) {
      this.initData.family2ndDegreeMembers.forEach((member: any, idx: number) => {
        const group = this.createFamilyMemberFormGroup(idx === 0 && this.pepDataForm.get('isPEP')?.value);
        group.patchValue({
          fullName: member.fullName ?? '',
          relationship: member.relationship ?? '',
        });
        family2ndDegreeArray.push(group);
      });
    }
  }
}
